import React, { useState, useEffect } from 'react';
import { atlasClient } from '../../../api/atlasClient';
import { SparkSuggestedResource } from '../types/sparkTypes';
import styles from '../styles/SuggestionCard.module.css';
import cardStyles from '../styles/MarketLogicDisplayCard.module.css';
import { DeepSightsIcon } from './SparkIcons';

interface SuggestionCardProps {
    suggestion: SparkSuggestedResource;
    isSelected: boolean;
    onToggleSelect: (fqn: string, isSelected: boolean) => void;
    isDisabled?: boolean;
}

const SuggestionCard: React.FC<SuggestionCardProps> = ({
    suggestion,
    isSelected,
    onToggleSelect,
    isDisabled = false
}) => {
    const displayName = suggestion.name || suggestion.fqn || 'Suggested Article';
    const previewText = suggestion.comment || '';
    const contextText = suggestion.context || '';
    const hasHeroImage = !!suggestion.hero_image_id;

    const [heroImageUrl, setHeroImageUrl] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (suggestion.hero_image_id) {
            setHeroImageUrl(atlasClient.getAssetUrl(suggestion.hero_image_id, { full: true }));
        } else {
            setHeroImageUrl(undefined);
        }
    }, [suggestion.hero_image_id]);

    const handleCardClick = () => {
        if (!isDisabled) {
            onToggleSelect(suggestion.fqn, !isSelected);
        }
    };

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation();
        if (!isDisabled) {
            onToggleSelect(suggestion.fqn, e.target.checked);
        }
    };

    return (
        <li
            className={`${styles.cardWrapper} ${isSelected ? styles.selected : ''}`}
            onClick={handleCardClick}
            title={`Select ${displayName}`}
        >
            {/* Selection overlay with checkbox */}
            <div className={styles.selectionOverlay}>
                <input
                    type="checkbox"
                    className={styles.selectionCheckbox}
                    checked={isSelected}
                    onChange={handleCheckboxChange}
                    onClick={(e) => e.stopPropagation()}
                    disabled={isDisabled}
                />
            </div>

            {/* Card content - similar to MarketLogicDisplayCard */}
            <div className={`${cardStyles.cardContainer} ${isSelected ? styles.noHoverScale : ''}`}>
                {hasHeroImage && heroImageUrl && (
                    <div className={cardStyles.imageContainer}>
                        <img
                            src={heroImageUrl}
                            alt={displayName}
                            className={cardStyles.heroImage}
                            loading="lazy"
                        />
                    </div>
                )}

                <div className={cardStyles.contentContainer}>
                    <h3 className={cardStyles.title}>{displayName}</h3>
                    {previewText && (
                        <p className={cardStyles.preview}>{previewText}</p>
                    )}
                    <div className={cardStyles.sourceBadge}>
                        <span className={cardStyles.sourceBadgeIcon}><DeepSightsIcon fontSize="small"/></span>
                        <span className={cardStyles.sourceBadgeText}>{contextText || 'DeepSights Profile'}</span>
                    </div>
                </div>
            </div>
        </li>
    );
};

export default SuggestionCard;
